/**
 * WelcomeScreen Component
 * Initial screen explaining survey purpose and collecting/confirming employee info
 * 
 * Two modes:
 * 1. Pre-filled mode (from admin link): Shows employee details for confirmation
 * 2. Manual mode (direct access): Collects employee info via form
 */

import React, { useState, useRef } from 'react';

/**
 * Welcome and information screen with employee info form or confirmation
 * @param {Object} props
 * @param {Function} props.onStart - Callback when form is submitted (manual mode)
 * @param {Function} props.onStartWithFile - Callback when form with file is submitted
 * @param {Function} props.onConfirm - Callback when details are confirmed (pre-filled mode)
 * @param {boolean} props.isLoading - Loading state for submit button
 * @param {Object} props.prefilledData - Pre-filled employee data from admin (if any)
 */
function WelcomeScreen({ onStart, onStartWithFile, onConfirm, isLoading, prefilledData }) {
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        department: '',
        jobDescription: '',
    });
    const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
    const [useFileUpload, setUseFileUpload] = useState(true); // Default to file upload
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);
    
    // Check if we have pre-filled data from admin
    const isPrefilledMode = prefilledData && prefilledData.employee_name;

    // Allowed file types
    const ALLOWED_FILE_TYPES = [
        'text/plain',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/markdown'
    ];
    const ALLOWED_EXTENSIONS = '.txt,.pdf,.doc,.docx,.md';

    /**
     * Handles form field changes (manual mode only)
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    /**
     * Handles file selection for job description
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.name.match(/\.(txt|pdf|doc|docx|md)$/i)) {
                setErrors(prev => ({
                    ...prev,
                    jobDescriptionFile: 'Please upload a PDF, Word document, or text file'
                }));
                return;
            }
            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    jobDescriptionFile: 'File is too large. Maximum size is 5MB'
                }));
                return;
            }
            setJobDescriptionFile(file);
            setErrors(prev => ({ ...prev, jobDescriptionFile: null }));
        }
    };

    /**
     * Removes selected file
     */
    const handleRemoveFile = () => {
        setJobDescriptionFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Validates form data (manual mode only)
     */
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Please enter your name';
        }
        if (!formData.role.trim()) {
            newErrors.role = 'Please enter your role/title';
        }
        if (!formData.department.trim()) {
            newErrors.department = 'Please enter your department';
        }
        // Job description file is required when using file upload mode
        if (useFileUpload && !jobDescriptionFile) {
            newErrors.jobDescriptionFile = 'Please upload your job description';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handles form submission (manual mode)
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            if (useFileUpload && jobDescriptionFile && onStartWithFile) {
                // Submit with file
                onStartWithFile(formData, jobDescriptionFile);
            } else {
                // Submit with text (or no JD)
                onStart(formData);
            }
        }
    };

    /**
     * Handles confirmation button click (pre-filled mode)
     */
    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
    };

    return (
        <div className="welcome-screen">
            <div className="welcome-content">
                {/* Header */}
                <div className="welcome-header">
                    <img src="/logo.jpg" alt="Salt & Straw" className="welcome-logo" style={{ height: '50px', marginBottom: '16px' }} onError={(e) => { e.target.style.display='none'; }} />
                    <h1 style={{ fontFamily: 'var(--font-family-heading)' }}>Work Study</h1>
                    <p className="subtitle">Understanding How You Really Work</p>
                </div>

                {/* Introduction */}
                <div className="welcome-intro">
                    {isPrefilledMode ? (
                        <>
                            <h2>Welcome, {prefilledData.employee_name}!</h2>
                            <p>
                                People Operations has set up this Work Study specifically for you. 
                                It's designed to help us understand how you actually spend 
                                your time at work — not what your job description says, but what 
                                your typical days really look like.
                            </p>
                        </>
                    ) : (
                        <>
                            <h2>Welcome!</h2>
                            <p>
                                This Work Study is designed to help us understand how you actually spend 
                                your time at work — not what your job description says, but what 
                                your typical days really look like.
                            </p>
                        </>
                    )}
                    
                    <div className="info-box">
                        <h3>📋 What to Expect</h3>
                        <ul>
                            <li>
                                <strong>20 thoughtful questions</strong> about your daily work, 
                                tools, and processes
                            </li>
                            <li>
                                <strong>Conversational format</strong> — answer naturally, 
                                like you're explaining to a curious colleague
                            </li>
                            <li>
                                <strong>AI-powered follow-ups</strong> — you may receive a few 
                                clarifying questions to better understand your responses
                            </li>
                            <li>
                                <strong>~30-45 minutes</strong> to complete thoughtfully
                            </li>
                        </ul>
                    </div>

                    <div className="info-box purpose-box">
                        <h3>🎯 Purpose</h3>
                        <p>
                            This is <strong>exploratory, not evaluative</strong>. We're looking for:
                        </p>
                        <ul>
                            <li>Where your time actually goes</li>
                            <li>Tasks that might benefit from automation or AI assistance</li>
                            <li>Gaps between job descriptions and reality</li>
                            <li>Ways we can better support you in your role</li>
                        </ul>
                        <p className="reassurance">
                            There are no right or wrong answers. Your honest insights help us 
                            understand how to better support you and your team.
                        </p>
                    </div>
                </div>

                {/* Pre-filled confirmation OR Manual form */}
                {isPrefilledMode ? (
                    <div className="confirmation-section">
                        <h3>Please Confirm Your Details</h3>
                        <p className="form-intro">We have the following information on file:</p>
                        
                        <div className="prefilled-details">
                            <div className="detail-row">
                                <span className="detail-label">Name:</span>
                                <span className="detail-value">{prefilledData.employee_name}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Email:</span>
                                <span className="detail-value">{prefilledData.employee_email}</span>
                            </div>
                            {prefilledData.employee_id && (
                                <div className="detail-row">
                                    <span className="detail-label">Employee ID:</span>
                                    <span className="detail-value">{prefilledData.employee_id}</span>
                                </div>
                            )}
                            <div className="detail-row">
                                <span className="detail-label">Role:</span>
                                <span className="detail-value">{prefilledData.role_title}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Department:</span>
                                <span className="detail-value">{prefilledData.department}</span>
                            </div>
                        </div>
                        
                        <button 
                            className="start-button confirm-button"
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="button-spinner"></span>
                                    Starting...
                                </>
                            ) : (
                                <>
                                    Yes, that's me! Let's begin
                                    <span className="arrow">→</span>
                                </>
                            )}
                        </button>
                        
                        <p className="wrong-person-note">
                            Not you? Please contact People Operations to get the correct work study link.
                        </p>
                    </div>
                ) : (
                    /* Manual Form */
                    <form onSubmit={handleSubmit} className="welcome-form">
                        <h3>Let's Get Started</h3>
                        <p className="form-intro">First, tell us a bit about yourself:</p>

                        <div className="form-group">
                            <label htmlFor="name">Your Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="e.g., Sarah Johnson"
                                className={errors.name ? 'error' : ''}
                                disabled={isLoading}
                            />
                            {errors.name && <span className="error-text">{errors.name}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="role">Your Role/Title *</label>
                            <input
                                type="text"
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                placeholder="e.g., Senior Marketing Manager"
                                className={errors.role ? 'error' : ''}
                                disabled={isLoading}
                            />
                            {errors.role && <span className="error-text">{errors.role}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="department">Department *</label>
                            <input
                                type="text"
                                id="department"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                placeholder="e.g., Marketing"
                                className={errors.department ? 'error' : ''}
                                disabled={isLoading}
                            />
                            {errors.department && <span className="error-text">{errors.department}</span>}
                        </div>

                        <div className="form-group job-description-group">
                            <label>
                                Job Description *
                                <span className="optional-hint">
                                    Upload your official JD — this helps us personalize the work study to your role
                                </span>
                            </label>
                            
                            {/* Toggle between file upload and text input */}
                            <div className="jd-input-toggle">
                                <button
                                    type="button"
                                    className={`toggle-btn ${useFileUpload ? 'active' : ''}`}
                                    onClick={() => setUseFileUpload(true)}
                                    disabled={isLoading}
                                >
                                    📁 Upload File
                                </button>
                                <button
                                    type="button"
                                    className={`toggle-btn ${!useFileUpload ? 'active' : ''}`}
                                    onClick={() => setUseFileUpload(false)}
                                    disabled={isLoading}
                                >
                                    ✍️ Paste Text
                                </button>
                            </div>

                            {useFileUpload ? (
                                <div className="file-upload-area">
                                    {!jobDescriptionFile ? (
                                        <div 
                                            className={`file-drop-zone ${errors.jobDescriptionFile ? 'error' : ''}`}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                accept={ALLOWED_EXTENSIONS}
                                                style={{ display: 'none' }}
                                                disabled={isLoading}
                                            />
                                            <div className="drop-zone-content">
                                                <span className="upload-icon">📄</span>
                                                <p>Click to upload or drag and drop</p>
                                                <span className="file-types">PDF, Word, or text files (max 5MB)</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="file-selected">
                                            <div className="file-info">
                                                <span className="file-icon">📄</span>
                                                <span className="file-name">{jobDescriptionFile.name}</span>
                                                <span className="file-size">
                                                    ({(jobDescriptionFile.size / 1024).toFixed(1)} KB)
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                className="remove-file-btn"
                                                onClick={handleRemoveFile}
                                                disabled={isLoading}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                    {errors.jobDescriptionFile && (
                                        <span className="error-text">{errors.jobDescriptionFile}</span>
                                    )}
                                </div>
                            ) : (
                                <textarea
                                    id="jobDescription"
                                    name="jobDescription"
                                    value={formData.jobDescription}
                                    onChange={handleChange}
                                    placeholder="Paste your job description here..."
                                    rows={6}
                                    disabled={isLoading}
                                />
                            )}
                        </div>

                        <button 
                            type="submit" 
                            className="start-button"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="button-spinner"></span>
                                    Starting...
                                </>
                            ) : (
                                <>
                                    Start Work Study
                                    <span className="arrow">→</span>
                                </>
                            )}
                        </button>
                    </form>
                )}

                {/* Footer */}
                <div className="welcome-footer">
                    <p>
                        After completing this work study, People Operations will review your responses 
                        and share findings with you in a follow-up conversation. Questions? Contact your manager.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default WelcomeScreen;
